import { useState, useCallback } from 'react';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor, registerPlugin } from '@capacitor/core';

// Register the custom LocationSettings native plugin
const LocationSettings = registerPlugin('LocationSettings');

/**
 * Custom hook for handling geolocation functionality
 * Works on both web browsers and native platforms
 * @returns {Object} Geolocation state and methods
 */
const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [isLocationEnabled, setIsLocationEnabled] = useState(true);

  const isNative = Capacitor.isNativePlatform();

  /**
   * Check geolocation permission status
   */
  const checkPermissions = useCallback(async () => {
    try {
      if (isNative) {
        const permission = await Geolocation.checkPermissions();
        setPermissionStatus(permission.location);
        return permission.location;
      } else {
        // Browser - check if geolocation is available
        if ('geolocation' in navigator) {
          setPermissionStatus('prompt');
          return 'prompt';
        }
        return 'denied';
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setError(err.message || 'Failed to check permissions');
      return 'denied';
    }
  }, [isNative]);

  /**
   * Request geolocation permission
   */
  const requestPermissions = useCallback(async () => {
    try {
      if (isNative) {
        const permission = await Geolocation.requestPermissions();
        setPermissionStatus(permission.location);
        return permission.location;
      } else {
        // Browser - permission is requested when getCurrentPosition is called
        setPermissionStatus('prompt');
        return 'prompt';
      }
    } catch (err) {
      console.error('Error requesting permissions:', err);
      setError(err.message || 'Failed to request permissions');
      return 'denied';
    }
  }, [isNative]);

  /**
   * Open device location settings (opens the actual GPS/Location toggle screen)
   */
  const openLocationSettings = useCallback(async () => {
    if (isNative) {
      try {
        // Use native plugin to open Android Location Settings (ACTION_LOCATION_SOURCE_SETTINGS)
        await LocationSettings.openLocationSettings();
      } catch (err) {
        console.error('Error opening location settings:', err);
      }
    }
  }, [isNative]);

  /**
   * Show the native in-app "Turn on location" dialog (like Blinkit/Zepto/Uber).
   * Uses Google's SettingsClient — user taps "Turn on" and GPS is enabled directly.
   * @returns {Promise<boolean>} true if GPS was enabled, false if user declined
   */
  const requestEnableLocation = useCallback(async () => {
    if (isNative) {
      try {
        const result = await LocationSettings.requestEnableLocation();
        const enabled = result.enabled;
        setIsLocationEnabled(enabled);
        console.log('📍 requestEnableLocation result:', enabled);
        return enabled;
      } catch (err) {
        console.error('Error requesting location enable:', err);
        return false;
      }
    }
    return true; // Browser — can't control GPS
  }, [isNative]);

  /**
   * Open the app's own settings page (Android Settings → App Info → Permissions).
   * Use when the user has permanently denied location permission ("Don't ask again").
   */
  const openAppSettings = useCallback(async () => {
    if (isNative) {
      try {
        await LocationSettings.openAppSettings();
      } catch (err) {
        console.error('Error opening app settings:', err);
      }
    }
  }, [isNative]);

  /**
   * Opens app settings and waits for the user to return, then re-checks permission.
   * Call this from a modal's "Go to Settings" button.
   * @returns {Promise<'granted'|'denied'>}
   */
  const goToSettingsAndWait = useCallback(async () => {
    await openAppSettings();

    return new Promise((resolve) => {
      const onResume = async () => {
        document.removeEventListener('resume', onResume);
        await new Promise(r => setTimeout(r, 500));
        try {
          const recheck = await Geolocation.checkPermissions();
          const status = recheck.location;
          setPermissionStatus(status);
          resolve(status === 'granted' ? 'granted' : 'denied');
        } catch {
          resolve('denied');
        }
      };
      document.addEventListener('resume', onResume);
      setTimeout(() => {
        document.removeEventListener('resume', onResume);
        resolve('denied');
      }, 60000);
    });
  }, [openAppSettings]);

  /**
   * Smart permission handler: tries native prompt first.
   * If permanently denied, returns 'permanently_denied' so the UI can show
   * a modal explaining the situation before sending the user to app settings.
   * @returns {Promise<'granted'|'denied'|'permanently_denied'>}
   */
  const ensureLocationPermission = useCallback(async () => {
    if (!isNative) return 'granted';

    try {
      // 1. Check current permission
      let permStatus = 'prompt';
      try {
        const currentPerm = await Geolocation.checkPermissions();
        permStatus = currentPerm.location;
      } catch (checkErr) {
        if (checkErr.message?.includes('not enabled') || checkErr.message?.includes('disabled')) {
          // GPS is off — not a permission issue, let caller handle via requestEnableLocation
          return 'prompt';
        }
        console.warn('checkPermissions threw:', checkErr.message);
      }

      // 2. Already granted
      if (permStatus === 'granted') {
        setPermissionStatus('granted');
        return 'granted';
      }

      // 3. Try the native prompt (works if not permanently denied)
      try {
        const permResult = await Geolocation.requestPermissions();
        permStatus = permResult.location;
        setPermissionStatus(permStatus);

        if (permStatus === 'granted') return 'granted';
      } catch (reqErr) {
        if (reqErr.message?.includes('not enabled') || reqErr.message?.includes('disabled')) {
          return 'prompt';
        }
        console.warn('requestPermissions threw:', reqErr.message);
      }

      // 4. Still denied → permanently denied (Android won't show prompt again).
      //    Return special status so the caller can show a modal first.
      console.log('📍 Permission permanently denied — UI should show settings modal');
      return 'permanently_denied';
    } catch (err) {
      console.error('ensureLocationPermission error:', err);
      return 'denied';
    }
  }, [isNative]);

  /**
   * Check if device location services (GPS) are actually enabled
   * Uses native Android LocationManager to check real GPS status
   */
  const checkLocationEnabled = useCallback(async () => {
    if (isNative) {
      try {
        // Use native plugin to check actual device GPS/Location status
        const result = await LocationSettings.isLocationEnabled();
        const enabled = result.enabled;
        setIsLocationEnabled(enabled);
        console.log('📍 Location services:', { enabled, gps: result.gpsEnabled, network: result.networkEnabled });
        return enabled;
      } catch (err) {
        console.error('Error checking location enabled:', err);
        // Fallback: try to get position and see if it fails
        setIsLocationEnabled(false);
        return false;
      }
    }
    return true; // Browser - assume enabled
  }, [isNative]);

  /**
   * Get current position using browser API
   */
  const getBrowserPosition = () => {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: position.timestamp,
          });
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          let errorType = 'UNKNOWN';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied';
              errorType = 'PERMISSION_DENIED';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location services disabled';
              errorType = 'LOCATION_DISABLED';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              errorType = 'TIMEOUT';
              break;
          }
          
          const err = new Error(errorMessage);
          err.type = errorType;
          reject(err);
        },
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        }
      );
    });
  };

  /**
   * Get current position
   * @param {Object} options - Geolocation options
   * @returns {Promise<Object>} Position coordinates
   */
  const getCurrentPosition = useCallback(async (options = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      let locationData;

      if (isNative) {
        // Native platform - use Capacitor Geolocation
        // Check current permission status first
        // Note: Capacitor's checkPermissions() can throw "Location services are not enabled"
        // when GPS is off. We handle this gracefully.
        let permStatus = 'prompt';
        try {
          const currentPerm = await Geolocation.checkPermissions();
          permStatus = currentPerm.location;
        } catch (checkErr) {
          // Capacitor can throw "Location services are not enabled" here even
          // though GPS was just turned on a moment ago (race condition).
          // Don't throw LOCATION_DISABLED — the caller already checked GPS.
          // Just log and continue; getCurrentPosition will fail below if GPS
          // is truly off.
          console.warn('checkPermissions threw (may be stale):', checkErr.message);
        }
        
        // If not yet granted, request permission (shows native dialog)
        if (permStatus !== 'granted') {
          try {
            const permission = await Geolocation.requestPermissions();
            permStatus = permission.location;
          } catch (reqErr) {
            // Same as above — may throw if GPS state is stale
            console.warn('requestPermissions threw:', reqErr.message);
            permStatus = 'denied';
          }
        }
        
        setPermissionStatus(permStatus);
        
        if (permStatus !== 'granted') {
          const err = new Error('Location permission denied');
          err.type = 'PERMISSION_DENIED';
          throw err;
        }

        try {
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 0,
            ...options,
          });

          locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: position.timestamp,
          };
        } catch (geoError) {
          // Check if it's a location services disabled error
          if (geoError.message && geoError.message.includes('disabled')) {
            setIsLocationEnabled(false);
            const err = new Error('Location services are disabled');
            err.type = 'LOCATION_DISABLED';
            throw err;
          }
          throw geoError;
        }
      } else {
        // Web browser - use native browser geolocation
        try {
          locationData = await getBrowserPosition();
        } catch (browserError) {
          if (browserError.type === 'LOCATION_DISABLED') {
            setIsLocationEnabled(false);
          }
          throw browserError;
        }
      }

      setLocation(locationData);
      setPermissionStatus('granted');
      setIsLocationEnabled(true);
      setIsLoading(false);
      return locationData;
    } catch (err) {
      console.error('Error getting position:', err);
      const errorMessage = err.message || 'Failed to get location';
      setError(errorMessage);
      
      if (err.type === 'LOCATION_DISABLED') {
        setIsLocationEnabled(false);
        setPermissionStatus('prompt');
      } else if (err.type === 'PERMISSION_DENIED') {
        setPermissionStatus('denied');
      } else {
        setPermissionStatus('denied');
      }
      
      setIsLoading(false);
      
      // Attach error type for better handling
      const error = new Error(errorMessage);
      error.type = err.type || 'UNKNOWN';
      throw error;
    }
  }, [isNative]);

  /**
   * Clear location and error state
   */
  const clearLocation = useCallback(() => {
    setLocation(null);
    setError(null);
  }, []);

  return {
    location,
    error,
    isLoading,
    permissionStatus,
    isLocationEnabled,
    getCurrentPosition,
    checkPermissions,
    requestPermissions,
    clearLocation,
    openLocationSettings,
    requestEnableLocation,
    checkLocationEnabled,
    openAppSettings,
    goToSettingsAndWait,
    ensureLocationPermission,
  };
};

export default useGeolocation;
